import { useAuth } from '@clerk/clerk-react';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { USER_ENDPOINTS } from '../config/api';
import type { UserData } from '../types/api.types';
import { extractApiErrorMessage } from '../utils/api';

export const userDataQueryKey = ['user', 'data'] as const;

type UseUserDataResult = Omit<UseQueryResult<UserData, Error>, 'data'> & {
  data: UserData | null;
};

export const useUserData = (): UseUserDataResult => {
  const { getToken, isSignedIn, isLoaded } = useAuth();

  const query = useQuery<UserData, Error>({
    queryKey: userDataQueryKey,
    enabled: Boolean(isLoaded && isSignedIn),
    queryFn: async () => {
      const token = await getToken();
      if (!token) {
        throw new Error('Authentication required. Please sign in.');
      }

      const response = await fetch(USER_ENDPOINTS.USER, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 401) {
        throw new Error('Authentication required. Please sign in.');
      }

      if (!response.ok) {
        const message = await extractApiErrorMessage(response, 'Failed to load your account details.');
        throw new Error(message);
      }

      const data: UserData = await response.json();
      return data;
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  return {
    ...query,
    data: query.data ?? null,
  };
};
